import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { ComponentPropsWithRef, ComponentType, ElementType, FocusEvent, FocusEventHandler, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, KeyboardEventHandler, PointerEventHandler, ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import type { InertiaLinkProps } from '@inertiajs/react'
import { Check } from '@phosphor-icons/react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { LoadingContent, usePressDepth } from './interior'

export type ClassValue = string | number | false | null | undefined

export function cn(...classes: ClassValue[]) {
  return classes.filter(Boolean).join(' ')
}

// `as="a"` renders an Inertia Link; otherwise a native <button> (type="button" unless overridden).
type PressHandlers = {
  onPointerDown?: PointerEventHandler<HTMLElement>
  onKeyDown?: KeyboardEventHandler<HTMLElement>
  onKeyUp?: KeyboardEventHandler<HTMLElement>
  onBlur?: FocusEventHandler<HTMLElement>
}
type LinkOwnProps = Omit<InertiaLinkProps, 'as'> & { as: 'a' }
type NativeButtonOwnProps = ComponentPropsWithRef<'button'> & { as?: 'button' }
export type PolymorphicButtonProps<Own> = (Own & PressHandlers & Omit<NativeButtonOwnProps, keyof Own | keyof PressHandlers>) | (Own & PressHandlers & Omit<LinkOwnProps, keyof Own | keyof PressHandlers>)

// The rendered element for a polymorphic control. Typed loosely: each Props union already checks what callers pass.
function surface(as: 'button' | 'a'): ElementType {
  return as === 'a' ? Link : as
}

export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'default' | 'small'

export type ButtonProps = PolymorphicButtonProps<{
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
  loading?: boolean
  loadingLabel?: ReactNode
  disabled?: boolean
  children?: ReactNode
}>

// Compact Geist controls: 32px by default, 26px for secondary inline actions.
export function Button({ as: As = 'button', variant = 'primary', size = 'default', className = '', loading, loadingLabel = 'Working…', disabled = false, children, onPointerDown, onKeyDown, onKeyUp, onBlur, ...props }: ButtonProps) {
  const press = usePressDepth({ disabled: disabled || loading })
  const base = 'control-button shrink-0 whitespace-nowrap transition-transform duration-150 motion-reduce:transform-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-45'
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-fg text-primary-fg hover:bg-muted-800',
    accent: 'bg-accent text-white hover:bg-accent-600',
    secondary: 'chrome-control text-fg hover:bg-muted-400',
    ghost: 'bg-transparent text-muted-700 hover:bg-hover hover:text-fg',
    danger: 'bg-danger-soft text-danger hover:bg-danger/15',
  }
  const Comp = surface(As)
  const defaults = As === 'button' ? { type: 'button' } : {}
  const inactive = disabled || loading
  const accessibleLabel = props['aria-label'] || (loading !== undefined && typeof children === 'string' ? children : undefined)
  return (
    <Comp
      ref={press.ref}
      className={cn(base, variants[variant], size === 'small' && 'min-h-[26px] rounded-row py-0.5 text-xs', press.pressed && 'translate-y-px', className)}
      disabled={As === 'button' ? inactive : undefined}
      aria-disabled={inactive || undefined}
      aria-busy={loading || undefined}
      aria-label={accessibleLabel}
      data-pressed={press.pressed ? '' : undefined}
      onPointerDown={(event: ReactPointerEvent<HTMLElement>) => { press.bind.onPointerDown(event); onPointerDown?.(event) }}
      onKeyDown={(event: ReactKeyboardEvent<HTMLElement>) => { press.bind.onKeyDown(event); onKeyDown?.(event) }}
      onKeyUp={(event: ReactKeyboardEvent<HTMLElement>) => { press.bind.onKeyUp(event); onKeyUp?.(event) }}
      onBlur={(event: FocusEvent<HTMLElement>) => { press.bind.onBlur(); onBlur?.(event) }}
      {...defaults}
      {...props}
    >
      <span className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2" aria-hidden="true" />
      {loading === undefined ? children : <LoadingContent loading={loading} label={loadingLabel}>{children}</LoadingContent>}
    </Comp>
  )
}

// Match the default control height, with an expanded touch target on coarse pointers.
export type IconButtonProps = PolymorphicButtonProps<{
  label: string
  shape?: 'circle' | 'square'
  className?: string
  disabled?: boolean
  children?: ReactNode
}>

export function IconButton({ as: As = 'button', label, shape = 'circle', className = '', children, disabled = false, onPointerDown, onKeyDown, onKeyUp, onBlur, ...props }: IconButtonProps) {
  const press = usePressDepth({ disabled })
  const Comp = surface(As)
  const defaults = As === 'button' ? { type: 'button' } : {}
  return (
    <Comp ref={press.ref} aria-label={label} title={label} disabled={As === 'button' ? disabled : undefined} data-pressed={press.pressed ? '' : undefined} onPointerDown={(event: ReactPointerEvent<HTMLElement>) => { press.bind.onPointerDown(event); onPointerDown?.(event) }} onKeyDown={(event: ReactKeyboardEvent<HTMLElement>) => { press.bind.onKeyDown(event); onKeyDown?.(event) }} onKeyUp={(event: ReactKeyboardEvent<HTMLElement>) => { press.bind.onKeyUp(event); onKeyUp?.(event) }} onBlur={(event: FocusEvent<HTMLElement>) => { press.bind.onBlur(); onBlur?.(event) }} className={cn('relative grid size-8 shrink-0 place-items-center text-muted-600 transition-transform duration-150 motion-reduce:transform-none hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-45', shape === 'circle' ? 'rounded-pill' : 'rounded-row', press.pressed && 'translate-y-px', className)} {...defaults} {...props}>
      <span className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2" aria-hidden="true" />
      {children}
    </Comp>
  )
}

export type CardProps = {
  className?: string
  children?: ReactNode
  tone?: 'surface' | 'result' | 'chip'
  padded?: boolean
}

export function Card({ className = '', children, tone = 'surface', padded = true }: CardProps) {
  return <div className={cn('min-w-0 rounded-pane', padded && 'p-4', tone === 'result' ? 'result-card' : tone === 'chip' ? 'bg-chip' : 'bg-recessed', className)}>{children}</div>
}

export type FieldProps = {
  label: ReactNode
  error?: string | string[] | null
  hint?: ReactNode
  className?: string
  children?: ReactNode
}

export function Field({ label, error, hint, className = '', children }: FieldProps) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="field-label">{label}</div>
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div role="status" className="field-hint text-danger">{Array.isArray(error) ? error.join(', ') : error}</div>}
    </label>
  )
}

// Fields sit on either ground: pane-coloured fill with a hairline so they read on surface cards too.
export const inputClass = 'control-input'

// The same status colors as the task marks: amber attention, green progress, red failure.
export type StatusTagProps = {
  status: string
  className?: string
}

export function StatusTag({ status, className = '' }: StatusTagProps) {
  const done = status === 'done' || status === 'succeeded'
  const failed = ['failed', 'cancelled', 'lost'].includes(status)
  const tone = status === 'needs_you' ? 'bg-warning-soft text-warning' : done || status === 'working' ? 'bg-success-soft text-success' : failed ? 'bg-danger-soft text-danger' : 'bg-control text-muted-700'
  return (
    <span className={cn('inline-flex h-6 shrink-0 items-center gap-1.5 rounded-tag px-2 text-xs font-medium whitespace-nowrap', tone, className)}>
      {done && <Check weight="bold" className="size-3 shrink-0" aria-hidden="true" />}
      {status === 'working' && <span className="pulse-dot size-1.5 rounded-full bg-success" aria-hidden="true" />}
      {done ? 'Done' : String(status).replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())}
    </span>
  )
}


export type EmptyProps = {
  className?: string
  children?: ReactNode
}

export function Empty({ className = '', children }: EmptyProps) {
  return <div className={cn('px-4 py-8 text-center text-base/6 text-pretty text-muted-600 sm:text-sm/6', className)}>{children}</div>
}

// Small count badge (needs-you counts, unread counts).
export type CountBadgeProps = {
  children?: ReactNode
  className?: string
}

export function CountBadge({ children, className = '' }: CountBadgeProps) {
  return <span className={cn('inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-tag bg-accent-100 px-1.5 text-[0.6875rem] font-semibold text-accent-700 tabular-nums dark:text-accent-300', className)}>{children}</span>
}

// Interior-inspired anchored surface: viewport collision handling, focus restoration, and motion.
export type PopoverSide = 'top' | 'bottom' | 'left' | 'right'

export type PopoverProps = {
  trigger: (open: boolean, toggle: () => void) => ReactNode
  align?: 'start' | 'center' | 'end'
  side?: PopoverSide
  label?: string
  wrapperClassName?: string
  className?: string
  children?: ReactNode
}

export function Popover({ trigger, align = 'start', side = 'bottom', label = 'Menu', wrapperClassName = '', className = '', children }: PopoverProps) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const reduced = useReducedMotion()
  const anchor = useRef<HTMLDivElement>(null)
  const floating = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const resolvedSide = useRef<PopoverSide>(side)

  const update = useCallback(() => {
    if (!anchor.current || !floating.current || !panel.current) return
    const triggerRect = anchor.current.getBoundingClientRect()
    const panelRect = panel.current.getBoundingClientRect()
    const padding = 8
    const offset = 6
    const room = {
      top: triggerRect.top - padding,
      bottom: window.innerHeight - triggerRect.bottom - padding,
      left: triggerRect.left - padding,
      right: window.innerWidth - triggerRect.right - padding,
    }
    const opposite: Record<PopoverSide, PopoverSide> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }
    const wanted = ['top', 'bottom'].includes(side) ? panelRect.height + offset : panelRect.width + offset
    const at = room[side] < wanted && room[opposite[side]] > room[side] ? opposite[side] : side
    resolvedSide.current = at
    const horizontal = ['top', 'bottom'].includes(at)
    let left = horizontal
      ? align === 'end' ? triggerRect.right - panelRect.width : align === 'center' ? triggerRect.left + (triggerRect.width - panelRect.width) / 2 : triggerRect.left
      : at === 'left' ? triggerRect.left - panelRect.width - offset : triggerRect.right + offset
    let top = horizontal
      ? at === 'top' ? triggerRect.top - panelRect.height - offset : triggerRect.bottom + offset
      : align === 'end' ? triggerRect.bottom - panelRect.height : align === 'center' ? triggerRect.top + (triggerRect.height - panelRect.height) / 2 : triggerRect.top
    left = Math.min(Math.max(padding, left), Math.max(padding, window.innerWidth - panelRect.width - padding))
    top = Math.min(Math.max(padding, top), Math.max(padding, window.innerHeight - panelRect.height - padding))
    floating.current.style.left = `${Math.round(left)}px`
    floating.current.style.top = `${Math.round(top)}px`
  }, [align, side])

  useLayoutEffect(() => { if (open) update() }, [open, update])
  useEffect(() => {
    if (!open) return undefined
    const onDown = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : null
      if (!anchor.current?.contains(target) && !panel.current?.contains(target)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      anchor.current?.querySelector<HTMLElement>('button, a, [tabindex]')?.focus({ preventScroll: true })
    }
    const schedule = () => requestAnimationFrame(update)
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
    }
  }, [open, update])

  return (
    <div ref={anchor} className={cn('relative inline-flex min-w-0 max-w-full', wrapperClassName)}>
      {trigger(open, () => setOpen((o) => !o))}
      <AnimatePresence>
        {open && (
          <div ref={floating} className="fixed top-0 left-0 z-50">
            <motion.div
              ref={panel}
              id={id}
              role="dialog"
              aria-label={label}
              tabIndex={-1}
              initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: resolvedSide.current === 'top' ? 4 : -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
              transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 }}
              className={cn('chrome-popover min-w-52 max-w-[calc(100vw-1rem)] max-h-[min(28rem,calc(100dvh-1rem))] overflow-y-auto rounded-pane p-1.5 outline-none', className)}
              onClick={(event) => { if (event.target instanceof Element && event.target.closest('[data-close]')) setOpen(false) }}
            >
              {children}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// One row inside a Popover.
export type MenuItemIcon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>

export type MenuItemProps = PolymorphicButtonProps<{
  icon?: MenuItemIcon
  badge?: number
  danger?: boolean
  className?: string
  children?: ReactNode
}>

export function MenuItem({ as: As = 'button', icon: Icon, badge = 0, danger = false, className = '', children, ...props }: MenuItemProps) {
  const Comp = surface(As)
  const defaults = As === 'button' ? { type: 'button' } : {}
  return (
    <Comp data-close role="menuitem" className={cn('flex min-h-12 w-full items-center gap-2.5 rounded-row px-2.5 text-left text-base hover:bg-hover focus-visible:bg-hover disabled:pointer-events-none disabled:opacity-45 sm:min-h-8 sm:text-sm', danger ? 'text-danger' : 'text-fg', className)} {...defaults} {...props}>
      {Icon && <Icon className={cn('size-4 shrink-0', danger ? 'text-danger' : 'text-muted-600')} aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {badge > 0 && <CountBadge>{badge}</CountBadge>}
    </Comp>
  )
}
